import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { teacherChatApi } from '@/services/api/teacherChat'
import { useAuthStore } from '@/store/authStore'
import { ChatListPanel } from '@/components/chat/ChatListPanel'
import { ChatConversationPanel } from '@/components/chat/ChatConversationPanel'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'

export function TeacherChatsPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useAuthStore(state => state.user)
  const currentTeacherId = user?.teacher_id

  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false)

  const { data: chatsData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['teacher-chats'],
    queryFn: () => teacherChatApi.getChats(),
    refetchInterval: () => (document.hidden ? false : 10000),
  })

  const chats = chatsData?.chats || []
  const selectedChat = chats.find(c => c.request_id === selectedRequestId) || null

  const handleSelectChat = (requestId: string) => {
    setSelectedRequestId(requestId)
    setIsMobileChatOpen(true)
    setSearchParams({ selected: requestId }, { replace: true })
    teacherChatApi.markAsRead(requestId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['teacher-chats'] })
        queryClient.invalidateQueries({ queryKey: ['teacher-chats-unread-count'] })
      })
      .catch(() => { /* ignore */ })
  }

  const handleCloseMobileChat = () => {
    setIsMobileChatOpen(false)
  }

  // Sync selected from URL if changed externally
  useEffect(() => {
    const fromUrl = searchParams.get('selected')
    if (fromUrl && fromUrl !== selectedRequestId) {
      setSelectedRequestId(fromUrl)
      setIsMobileChatOpen(true)
      // Deep-Link aus der Stellvertretungsliste: Staff-Participant + Profil
      // on demand erzeugen (mark-read committet), damit der Chat nach dem
      // Refetch in der Übersicht erscheint (bb-v2 3.4/7.4).
      teacherChatApi.markAsRead(fromUrl)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['teacher-chats'] })
          queryClient.invalidateQueries({ queryKey: ['teacher-chats-unread-count'] })
        })
        .catch(() => { /* ignore */ })
    }
  }, [searchParams, selectedRequestId, queryClient])

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-[calc(100vh-3.5rem)] min-w-0 overflow-hidden">
        <div className="h-14 bg-white border-b border-gray-200 flex items-center px-4 md:px-6 justify-between shrink-0">
          <h1 className="text-lg font-semibold text-gray-900">Chats</h1>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            title="Aktualisieren"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
          {/* Desktop / Tablet: Split-View */}
          <div className="hidden md:flex flex-1 min-h-0 min-w-0 overflow-hidden">
            <ChatListPanel
              chats={chats}
              selectedRequestId={selectedRequestId}
              onSelectChat={handleSelectChat}
              isLoading={isLoading}
            />
            <ChatConversationPanel
              chat={selectedChat}
              currentTeacherId={currentTeacherId}
            />
          </div>

          {/* Mobile: Liste */}
          <div className="md:hidden flex-1 min-h-0 min-w-0 overflow-hidden">
            <ChatListPanel
              chats={chats}
              selectedRequestId={selectedRequestId}
              onSelectChat={handleSelectChat}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Mobile: Chat Slide-Over */}
        {isMobileChatOpen && selectedChat && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div
              className="absolute inset-0 bg-black/30"
              onClick={handleCloseMobileChat}
            />
            <div className="relative ml-auto w-full bg-gray-50 flex flex-col shadow-xl animate-slide-in">
              <ChatConversationPanel
                chat={selectedChat}
                currentTeacherId={currentTeacherId}
                onClose={handleCloseMobileChat}
              />
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}
